/**
 * Offline storage for Peta Global pin data & Antar Surat delivery lists.
 * Uses a dedicated IndexedDB ("PetaOfflineDB") so it never conflicts with the
 * payments offline DB ("PDAMOfflineDB").
 *
 * Stores:
 *  - customers       : cached map pins ({ customer_id, ...})  → renders the map offline & instantly
 *  - meta            : small key/value metadata (savedAt, count)
 *  - delivery_lists  : cached Antar Surat lists ({ id, ...})
 *  - delivery_queue  : queued offline status/photo edits awaiting sync
 */

const DB_NAME = "PetaOfflineDB";
const DB_VERSION = 1;

const STORES = {
  CUSTOMERS: "customers",
  META: "meta",
  DELIVERY_LISTS: "delivery_lists",
  DELIVERY_QUEUE: "delivery_queue",
};

const openDB = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORES.CUSTOMERS)) {
        db.createObjectStore(STORES.CUSTOMERS, { keyPath: "customer_id" });
      }
      if (!db.objectStoreNames.contains(STORES.META)) {
        db.createObjectStore(STORES.META, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(STORES.DELIVERY_LISTS)) {
        db.createObjectStore(STORES.DELIVERY_LISTS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORES.DELIVERY_QUEUE)) {
        db.createObjectStore(STORES.DELIVERY_QUEUE, { keyPath: "qid" });
      }
    };
  });

const txDone = (tx) =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

// ---------- CUSTOMERS (map pins) ----------

export const savePetaCustomers = async (customers) => {
  try {
    const db = await openDB();
    const tx = db.transaction([STORES.CUSTOMERS, STORES.META], "readwrite");
    const store = tx.objectStore(STORES.CUSTOMERS);
    store.clear();
    let count = 0;
    for (const c of customers || []) {
      if (!c || !c.customer_id) continue;
      store.put(c);
      count += 1;
    }
    tx.objectStore(STORES.META).put({
      key: "customers_meta",
      count,
      savedAt: new Date().toISOString(),
    });
    await txDone(tx);
    return count;
  } catch (err) {
    console.error("savePetaCustomers error:", err);
    return 0;
  }
};

export const getPetaCustomers = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.CUSTOMERS, "readonly");
    const store = tx.objectStore(STORES.CUSTOMERS);
    return await new Promise((resolve) => {
      const r = store.getAll();
      r.onsuccess = () => resolve(r.result || []);
      r.onerror = () => resolve([]);
    });
  } catch (err) {
    console.error("getPetaCustomers error:", err);
    return [];
  }
};

export const getPetaMeta = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.META, "readonly");
    const store = tx.objectStore(STORES.META);
    return await new Promise((resolve) => {
      const r = store.get("customers_meta");
      r.onsuccess = () => resolve(r.result || null);
      r.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
};

// ---------- DELIVERY LISTS ----------

export const saveDeliveryListOffline = async (list) => {
  if (!list || !list.id) return false;
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.DELIVERY_LISTS, "readwrite");
    tx.objectStore(STORES.DELIVERY_LISTS).put({ ...list, _cachedAt: new Date().toISOString() });
    await txDone(tx);
    return true;
  } catch (err) {
    console.error("saveDeliveryListOffline error:", err);
    return false;
  }
};

export const getDeliveryListOffline = async (id) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.DELIVERY_LISTS, "readonly");
    const store = tx.objectStore(STORES.DELIVERY_LISTS);
    return await new Promise((resolve) => {
      const r = store.get(id);
      r.onsuccess = () => resolve(r.result || null);
      r.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
};

export const getAllDeliveryListsOffline = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.DELIVERY_LISTS, "readonly");
    const store = tx.objectStore(STORES.DELIVERY_LISTS);
    return await new Promise((resolve) => {
      const r = store.getAll();
      r.onsuccess = () => resolve(r.result || []);
      r.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
};

// Recompute counts for a list's items (pending/delivered/not_found/promised_pay/lunas/total)
export const computeCounts = (items = []) => {
  const counts = { total: items.length, pending: 0, delivered: 0, not_found: 0, promised_pay: 0, lunas: 0, putus: 0 };
  for (const it of items) {
    const s = it.status || "pending";
    if (s === "delivered") counts.delivered += 1;
    else if (s === "not_found") counts.not_found += 1;
    else if (s === "promised_pay") counts.promised_pay += 1;
    else if (s === "lunas") counts.lunas += 1;
    else if (s === "putus") counts.putus += 1;
    else counts.pending += 1;
  }
  return counts;
};

// ---------- OFFLINE EDIT QUEUE ----------

export const enqueueDeliveryEdit = async (listId, customer_id, payload) => {
  try {
    const db = await openDB();
    const tx = db.transaction([STORES.DELIVERY_QUEUE, STORES.DELIVERY_LISTS], "readwrite");
    const qStore = tx.objectStore(STORES.DELIVERY_QUEUE);
    const qid = `${listId}__${customer_id}`; // one pending edit per item (latest wins)
    qStore.put({
      qid,
      listId,
      customer_id,
      payload,
      created_at: new Date().toISOString(),
    });
    // Optimistically patch the cached list copy so offline UI stays consistent
    const listStore = tx.objectStore(STORES.DELIVERY_LISTS);
    const getReq = listStore.get(listId);
    getReq.onsuccess = () => {
      const list = getReq.result;
      if (list && Array.isArray(list.items)) {
        list.items = list.items.map((it) =>
          it.customer_id === customer_id ? { ...it, ...payload, _pendingSync: true } : it
        );
        list.counts = computeCounts(list.items);
        listStore.put(list);
      }
    };
    await txDone(tx);
    return qid;
  } catch (err) {
    console.error("enqueueDeliveryEdit error:", err);
    return null;
  }
};

export const getDeliveryQueue = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.DELIVERY_QUEUE, "readonly");
    const store = tx.objectStore(STORES.DELIVERY_QUEUE);
    return await new Promise((resolve) => {
      const r = store.getAll();
      r.onsuccess = () => resolve(r.result || []);
      r.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
};

export const removeQueueItem = async (qid) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.DELIVERY_QUEUE, "readwrite");
    tx.objectStore(STORES.DELIVERY_QUEUE).delete(qid);
    await txDone(tx);
    return true;
  } catch {
    return false;
  }
};

/**
 * Flush all queued offline delivery edits to the server.
 * Returns { synced, failed }.
 */
export const flushDeliveryQueue = async (apiBase, token) => {
  if (!navigator.onLine || !token) return { synced: 0, failed: 0 };
  const queue = await getDeliveryQueue();
  if (queue.length === 0) return { synced: 0, failed: 0 };
  let synced = 0;
  let failed = 0;
  for (const q of queue) {
    try {
      const res = await fetch(
        `${apiBase}/delivery-lists/${q.listId}/items/${encodeURIComponent(q.customer_id)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(q.payload),
        }
      );
      if (res.ok) {
        await removeQueueItem(q.qid);
        synced += 1;
      } else if (res.status === 404) {
        // List/item gone — drop the stale queue entry
        await removeQueueItem(q.qid);
        failed += 1;
      } else {
        failed += 1;
      }
    } catch {
      failed += 1; // still offline / network error — keep in queue
    }
  }
  return { synced, failed };
};

export const getPendingQueueCount = async () => (await getDeliveryQueue()).length;

export const isOnline = () => navigator.onLine;
